import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";

import { AuthProvider } from "./context/AuthContext";
import { SectorProvider } from "./context/SectorContext";
import { ClusterProvider } from "./context/ClusterContext";
import { ProgrammeProvider } from "./context/ProgrammeContext";
import { SkillProvider } from "./context/SkillContext";
import { PartnerProvider } from "./context/PartnerContext";

// Core UI
const Spinner = lazy(() => import("./components/Spinner"));
const AppLayout = lazy(() => import("./pages/AppLayout"));
const ScrollToTop = lazy(() => import("./pages/ScrollToTop"));
const GoogleAnalytics = lazy(() => import("./components/GoogleAnalytics"));
const CookiebotScript = lazy(() => import("./components/CookiebotScript"));

// Public pages
const Home = lazy(() => import("./pages/Home"));
const Sectors = lazy(() => import("./pages/Sectors"));
const SectorDetails = lazy(() => import("./pages/SectorDetails"));
const SkillCluster = lazy(() => import("./pages/SkillCluster"));
const ProgrammeDetail = lazy(() => import("./pages/ProgrammeDetail"));
const Accessibility = lazy(() => import("./pages/Accessibility"));
const Privacy = lazy(() => import("./pages/Privacy"));
const CookieNotice = lazy(() => import("./pages/CookieNotice"));
const PageNotFound = lazy(() => import("./pages/PageNotFound"));

// Admin grouped module
const AdminModule = lazy(() => import("./pages/admin/AdminModule"));

function App() {
  const providers = [
    AuthProvider,
    SectorProvider,
    ClusterProvider,
    SkillProvider,
    ProgrammeProvider,
    PartnerProvider,
  ];

  const wrapWithProviders = (children) =>
    providers.reduceRight(
      (acc, Provider) => <Provider>{acc}</Provider>,
      children
    );

  // Preload critical routes (example: Home, Sectors)
  useEffect(() => {
    import("./pages/Home");
    import("./pages/Sectors");
  }, []);

  return wrapWithProviders(
    <Router>
      <Suspense fallback={<Spinner />}>
        <ScrollToTop>
          <CookiebotScript />
          <GoogleAnalytics />
          <Routes>
            <Route path="/" element={<AppLayout />}>
              <Route index element={<Home />} />
              <Route path="/sectors" element={<Sectors />} />
              <Route path="/sectors/:sector" element={<SectorDetails />} />
              <Route
                path="/sectors/:sector/:cluster"
                element={<SkillCluster />}
              />
              <Route
                path="/sectors/:sector/:cluster/:programme"
                element={<ProgrammeDetail />}
              />
              <Route
                path="/programmes/:programme"
                element={<ProgrammeDetail />}
              />
              <Route
                path="/accessibility-statement"
                element={<Accessibility />}
              />
              <Route path="/privacy-notice" element={<Privacy />} />
              <Route path="/cookie-notice" element={<CookieNotice />} />
            </Route>

            {/* Admin routes in one lazy-loaded module */}
            <Route path="/*" element={<AdminModule />} />

            <Route path="*" element={<PageNotFound />} />
          </Routes>
        </ScrollToTop>
      </Suspense>
    </Router>
  );
}

export default App;
